function pad(n) {
  return String(n).padStart(2, "0");
}

const formatDay = (day) => `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;

function toDateParts(value) {
  if (!value) return null;
  const [y, m, d] = String(value).split("-").map((part) => parseInt(part, 10));
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export default function generateSlots({ startDate, endDate, weekdays, timesByWeekday } = {}) {
  const start = toDateParts(startDate);
  const end = toDateParts(endDate);
  const days = Array.isArray(weekdays) ? weekdays.map(Number) : [];
  if (!start || !end || days.length === 0 || end < start) return [];

  const byWeekday = timesByWeekday || {};
  const slots = [];

  for (let cursor = new Date(start); cursor <= end; cursor.setDate(cursor.getDate() + 1)) {
    const wday = cursor.getDay();
    if (!days.includes(wday)) continue;

    const date = formatDay(cursor);
    (byWeekday[wday] || []).forEach((range) => {
      slots.push({ date, start: range.start, end: range.end });
    });
  }

  return slots.sort((a, b) => (a.date === b.date ? a.start.localeCompare(b.start) : a.date.localeCompare(b.date)));
}

const DEFAULT_START = "18:00";
const DEFAULT_END = "21:00";
const MINUTES_PER_DAY = 24 * 60;
const toClock = (minutes) => `${pad(Math.floor(minutes / 60))}:${pad(minutes % 60)}`;
const toMinutes = (clock) => {
  const [h, m] = clock.split(":").map(Number);
  return h * 60 + m;
};

export function defaultTimeRange(durationMinutes) {
  const minutes = Number(durationMinutes);
  if (!Number.isInteger(minutes) || minutes <= 0 || minutes >= MINUTES_PER_DAY) return { start: DEFAULT_START, end: DEFAULT_END };
  const eveningStart = toMinutes(DEFAULT_START);
  if (eveningStart + minutes < MINUTES_PER_DAY) return { start: DEFAULT_START, end: toClock(eveningStart + minutes) };
  const start = Math.max(0, toMinutes(DEFAULT_END) - minutes);
  return { start: toClock(start), end: toClock(start + minutes) };
}

export function fitSessionsToSlots(sessions, slots) {
  let changed = false;
  const next = sessions.map((s, i) => {
    if (s.touched) return s;
    const slot = slots[i] || { date: "", start: "", end: "" };
    if (s.date === slot.date && s.start === slot.start && s.end === slot.end) return s;
    changed = true;
    return { ...s, date: slot.date, start: slot.start, end: slot.end };
  });
  return changed ? next : sessions;
}

const MAX_DAYS_TO_FILL = 3 * 366;

export function autoEndDate({ startDate, weekdays, timesByWeekday }, sessionCount) {
  const cursor = toDateParts(startDate);
  if (!cursor) return "";
  const days = (weekdays || []).map(Number);
  const needed = Math.max(sessionCount, 1);
  let filled = 0;
  for (let i = 0; i < MAX_DAYS_TO_FILL; i += 1) {
    if (days.includes(cursor.getDay())) filled += ((timesByWeekday || {})[cursor.getDay()] || []).length;
    if (filled >= needed) return formatDay(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  return "";
}

export function shiftDate(value, fromDate, toDateValue) {
  const date = toDateParts(value);
  const from = toDateParts(fromDate);
  const to = toDateParts(toDateValue);
  if (!date || !from || !to) return value;
  date.setDate(date.getDate() + Math.round((to - from) / 86400000));
  return formatDay(date);
}

export function resizeAutoEndDate(schedule, sessionCount) {
  if (!schedule.endDateAuto || !schedule.startDate || sessionCount === 0) return schedule;
  return { ...schedule, endDate: autoEndDate(schedule, sessionCount) || schedule.endDate };
}

export function scheduleForTemplate(schedule, { duration, sessionCount }) {
  const newDefault = { ...defaultTimeRange(duration), auto: true };
  const timesByWeekday = {};
  Object.keys(schedule.timesByWeekday || {}).forEach((wday) => {
    timesByWeekday[wday] = schedule.timesByWeekday[wday].map((range) => (range.auto ? newDefault : range));
  });
  return resizeAutoEndDate({ ...schedule, timesByWeekday }, sessionCount);
}
